import React from "react";
import { CREATE_GROUP_ORDER_STEPS } from ".";
import Button from "../Button";
import Radio from "../Forms/Radio";
import TextInput from "../Forms/TextInput";
import SimpleHeader from "../SimpleHeader";
import { DELIVERY_TYPES } from "../Topbar";
import Headline from "../Typography/Headline";
import { CreateGroupOrderPropTypes } from "./types";
import { Check, Info as InfoIcon } from "react-feather";
import { getSvgIcon } from "../../Helpers/get-assets";

const CreateGroupOrder = ({
    labels,
    heroImageSrc,
    name,
    setName,
    location,
    isPickup,
    timeslot,
    earliestPossibleTimeslot,
    isEarliestTimeslot,
    formErrors = {},
    step,
    groupOrderUrl,
    earlyOnLocationText,
    onBackClick = () => null,
    onDeliveryToggle = () => null,
    openTimeslots = () => null,
    selectEarliestTimeslot = () => null,
    submitGroupOrder = () => null,
    openQrCode = () => null,
    copyGroupOrderUrl = () => null,
    isSubmitting = false,
}) => {
    const ShareIcon = getSvgIcon("clipboard.svg");
    const QRIcon = getSvgIcon("share-qr.svg");
    const GlobeIcon = getSvgIcon("globe.svg");
    const ClockIcon = getSvgIcon("clock.svg");
    const FlagIcon = getSvgIcon("flag.svg");

    const renderTimeslotInfo = (timeslot) => (
        <>
            <p>{timeslot.slot}</p>
            {timeslot.isToday && (
                <div className="create-grp-order__option-info">
                    <InfoIcon />
                    {`${labels.orderLatestTo} ${timeslot.validTill} Uhr`}
                </div>
            )}
        </>
    );

    const renderInfoStep = () => (
        <div className="create-grp-order__left">
            <Headline size={4} className="create-grp-order__title">
                {labels.title}
            </Headline>
            <p className="create-grp-order__subtitle">{labels.subtitle}</p>
            <TextInput
                bindName="name"
                label={labels.enterName}
                onChange={setName}
                value={name}
                error={"name" in formErrors}
                errorMessage={formErrors?.name || ""}
            />

            {/* LOCATION SELECTION */}
            <span className="create-grp-order__subtitle create-grp-order__subtitle--delivery-info">
                {labels.whereToDeliver}
            </span>
            <div className="create-grp-order__options">
                <Radio
                    bindName="deliveryType"
                    value={false}
                    currentValue={isPickup}
                    handleOnClick={() =>
                        onDeliveryToggle(DELIVERY_TYPES.DELIVERY)
                    }>
                    <div className="create-grp-order__option">
                        <span>{labels[DELIVERY_TYPES.DELIVERY]}</span>
                        {!isPickup && <p>{location}</p>}
                    </div>
                    {!isPickup && (
                        <Check className="create-grp-order__option-check" />
                    )}
                </Radio>
                <hr />
                <Radio
                    bindName="deliveryType"
                    value={true}
                    currentValue={isPickup}
                    handleOnClick={() =>
                        onDeliveryToggle(DELIVERY_TYPES.PICKUP)
                    }>
                    <div className="create-grp-order__option">
                        <span>{labels[DELIVERY_TYPES.PICKUP]}</span>
                        <p>
                            {!isPickup ? labels.pickStoreForPickup : location}
                        </p>
                    </div>
                    {isPickup && (
                        <Check className="create-grp-order__option-check" />
                    )}
                </Radio>
            </div>

            {/* TIMESLOT SELECTION */}
            <span className="create-grp-order__subtitle create-grp-order__subtitle--delivery-info">
                {isPickup ? labels.whenToPickup : labels.whenToDelivery}
            </span>
            <div className="create-grp-order__options">
                <Radio
                    bindName="deliveryTime"
                    value={true}
                    currentValue={isEarliestTimeslot}
                    handleOnClick={selectEarliestTimeslot}>
                    <div className="create-grp-order__option">
                        <span>{labels.earliestPossible}</span>
                        {isEarliestTimeslot ? (
                            renderTimeslotInfo(earliestPossibleTimeslot)
                        ) : (
                            <p>{labels.pickEarliest}</p>
                        )}
                    </div>
                    {isEarliestTimeslot && (
                        <Check className="create-grp-order__option-check" />
                    )}
                </Radio>
                <hr />
                <Radio
                    handleOnClick={openTimeslots}
                    bindName="deliveryTime"
                    value={false}
                    currentValue={isEarliestTimeslot}>
                    <div className="create-grp-order__option">
                        <span>{labels.selectDeliverySlot}</span>
                        {isEarliestTimeslot ? (
                            <p>{labels.pickTimeslot}</p>
                        ) : (
                            <>
                                {!isPickup && <p>{earlyOnLocationText}</p>}
                                {renderTimeslotInfo(timeslot)}
                            </>
                        )}
                    </div>
                    {!isEarliestTimeslot && (
                        <Check className="create-grp-order__option-check" />
                    )}
                </Radio>
            </div>
            <Button
                label={labels.orderAsGroupCTA}
                onClick={submitGroupOrder}
                size="SM"
                loading={isSubmitting}
            />
        </div>
    );

    const renderShareStep = () => (
        <div className="create-grp-order__left">
            <Headline size={4} className="create-grp-order__title">
                {labels.orderTogetherTitle}
            </Headline>
            <p className="create-grp-order__subtitle create-grp-order__subtitle--share">
                {labels.orderTogetherSubtitle}
                <br />
                <strong>{labels.orderTogetherDisclaimer}</strong>
            </p>
            <section className="create-grp-order__share-section">
                <span>{labels.linkTitle}</span>
                <p>{labels.linkSubtitle}</p>
                <button
                    className="create-grp-order__share-qr"
                    onClick={openQrCode}>
                    <img src={QRIcon} alt="qr-share" />
                </button>
                <section className="create-grp-order__share-link">
                    <span>{groupOrderUrl}</span>
                    <button onClick={copyGroupOrderUrl}>
                        <img src={ShareIcon} alt="share-link" />
                    </button>
                </section>
            </section>
            <div className="create-grp-order__details">
                <div className="create-grp-order__detail">
                    <img src={GlobeIcon} />
                    <p>{labels.groupOrderTip}</p>
                </div>
                <div className="create-grp-order__detail">
                    <img src={ClockIcon} />
                    <p>
                        {labels.orderTill.replace(
                            "{TIME}",
                            timeslot.isToday
                                ? timeslot.validTill
                                : `${timeslot.deliveryDay}, ${timeslot.validTill}`
                        )}
                    </p>
                </div>
                <div className="create-grp-order__detail">
                    <img src={FlagIcon} />
                    <p>{labels.groupOrderWarning}</p>
                </div>
            </div>
            <Button label={labels.finish} onClick={onBackClick} size="SM" />
        </div>
    );

    const renderLeftSide = () => {
        switch (step) {
            case CREATE_GROUP_ORDER_STEPS.SHARE:
                return renderShareStep();
            case CREATE_GROUP_ORDER_STEPS.INFO:
            default:
                return renderInfoStep();
        }
    };

    return (
        <>
            <SimpleHeader
                onBackClick={onBackClick}
                showArrow={step !== CREATE_GROUP_ORDER_STEPS.INFO}
                showBack={step === CREATE_GROUP_ORDER_STEPS.INFO}
                backText="header.abort"
            />
            <div className="create-grp-order">
                {renderLeftSide()}
                <div className="create-grp-order__right">
                    <img
                        src={heroImageSrc}
                        className="create-grp-order__hero"
                        alt="group-order-hero"
                    />
                </div>
            </div>
        </>
    );
};

CreateGroupOrder.propTypes = CreateGroupOrderPropTypes;
export default CreateGroupOrder;
